import { useState, useMemo, useRef, useEffect } from "react";
import { rawTimeZones } from "@vvo/tzdb";
import { ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

interface TimezoneComboboxProps {
  value: string;
  onChange: (tz: string) => void;
  disabled?: boolean;
}

interface TzEntry {
  name: string;
  label: string;
  searchText: string;
}

const tzEntries: TzEntry[] = rawTimeZones.map((tz) => {
  const hours = Math.floor(Math.abs(tz.rawOffsetInMinutes) / 60);
  const mins = Math.abs(tz.rawOffsetInMinutes) % 60;
  const sign = tz.rawOffsetInMinutes >= 0 ? "+" : "-";
  const offset = `UTC${sign}${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;

  const cities = tz.mainCities.slice(0, 3).join(", ");
  const label = `(${offset}) ${tz.alternativeName} \u2014 ${cities}`;

  return {
    name: tz.name,
    label,
    searchText: `${tz.name} ${tz.alternativeName} ${tz.mainCities.join(" ")} ${tz.countryName} ${offset}`.toLowerCase(),
  };
});

export function TimezoneCombobox({ value, onChange, disabled }: TimezoneComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search) return tzEntries;
    const q = search.toLowerCase();
    return tzEntries.filter((e) => e.searchText.includes(q));
  }, [search]);

  const selectedLabel = useMemo(() => {
    const entry = tzEntries.find((e) => e.name === value);
    return entry?.label ?? value;
  }, [value]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal text-left h-auto min-h-10 py-2"
        >
          <span className="truncate text-sm">
            {value ? selectedLabel : "Select timezone..."}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        {/* Same muted fill + `.focus-ring` treatment as SearchableSelect —
            a bare <input> has no background token and renders white here in
            dark themes. */}
        <div className="relative flex items-center border-b p-2">
          <Search className="pointer-events-none absolute left-4 h-3.5 w-3.5 text-foreground/70" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search timezones..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-muted-foreground/30 bg-muted/60 py-1.5 pl-8 pr-2 text-sm text-foreground placeholder:text-foreground/60 focus-ring"
          />
        </div>
        <div ref={listRef} className="max-h-60 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No timezone found.</p>
          ) : (
            filtered.map((entry) => (
              <button
                key={entry.name}
                type="button"
                className={cn(
                  "w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent cursor-pointer",
                  entry.name === value && "bg-accent font-medium"
                )}
                onClick={() => {
                  onChange(entry.name);
                  setOpen(false);
                }}
              >
                {entry.label}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
